// EXPERIMENT #171270395
import React from 'react';
import PropTypes from 'prop-types';
import { hot } from 'react-hot-loader';

import { Formik } from 'formik';

import Button from 'components/button';
import { getAnswersFromStorage, storeAnswerToStorage } from '../storage_helpers';
import { trackQuestionAnswered, trackBackButtonClicked } from '../tracking_helpers';
import LPSurveyQuestion from './lp_survey_question';
import MultipleSelectSurveyQuestion from './multiple_select_survey_question';
import QUESTIONS_DEFAULT from '../constants/configurations';
import QUESTIONS_WITH_RANDOMIZED_QUESTIONS from '../constants/configurations_with_randomized_questions';
import LpHeader from './lp_header';
import ProcessingScreen from './processing_screen';

class LpSurveyManager extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      currentQuestion: 'RUN_PAYROLL',
      showProcessingScreen: false,
      helpfulInfoAnswers: [],
      particularFeaturesAnswers: [],
    };
  }

  getQuestions = () => {
    if (this.props.withRandomizedQuestion) {
      return QUESTIONS_WITH_RANDOMIZED_QUESTIONS; // EXPERIMENT GWTH-1797
    }
    return QUESTIONS_DEFAULT;
  };

  QUESTIONS = this.getQuestions();

  HELPFUL_INFO_QUESTION = 'HELPFUL_INFORMATION'; // EXPERIMENT GWTH-1797

  PARTICULAR_FEATURES = 'PARTICULAR_FEATURES'; // EXPERIMENT GWTH-1797

  PROCESSING_SCREEN_DELAY = 1000;

  setNextState = (nextQuestion, showProcessingScreenWithDelay) => {
    if (!nextQuestion) {
      if (showProcessingScreenWithDelay) {
        this.setState({ showProcessingScreen: true });
        setTimeout(() => {
          this.props.onSurveyFinish();
        }, this.PROCESSING_SCREEN_DELAY);
      } else {
        this.props.onSurveyFinish();
      }
    }

    this.setState({ currentQuestion: nextQuestion });
  };

  getNextState = (selectedAnswer, currentQuestion) => {
    const nextQuestionOptions = this.QUESTIONS[currentQuestion].next;

    let nextQuestionKey = nextQuestionOptions.default;
    if (this.QUESTIONS[currentQuestion].multi_select_answers) {
      selectedAnswer.forEach(answer => {
        if (answer in nextQuestionOptions) {
          nextQuestionKey = nextQuestionOptions[answer];
        }
      });
    } else if (selectedAnswer in nextQuestionOptions) {
      nextQuestionKey = nextQuestionOptions[selectedAnswer];
    }

    return nextQuestionKey;
  };

  getNextStateFromSession() {
    const savedAnswers = getAnswersFromStorage();
    let currentQuestion = 'RUN_PAYROLL';

    while (currentQuestion && savedAnswers[currentQuestion]) {
      currentQuestion = this.getNextState(savedAnswers[currentQuestion], currentQuestion);
    }
    return currentQuestion;
  }

  updateQuestionState(showProcessingScreenWithDelay) {
    const currentQuestionBasedOnSession = this.getNextStateFromSession();
    if (this.state.currentQuestion !== currentQuestionBasedOnSession) {
      this.setNextState(currentQuestionBasedOnSession, showProcessingScreenWithDelay);
    }
  }

  componentDidMount() {
    const { withRandomizedQuestion } = this.props;
    this.updateQuestionState(false);
    if (withRandomizedQuestion) {
      this.randomizeQuestions(); // EXPERIMENT GWTH-1797
    }
  }

  componentDidUpdate() {
    this.updateQuestionState(true);
  }

  randomizeArray = array => {
    return array.sort(() => Math.random() - 0.5);
  };

  randomizeHelper = question => {
    const questionObject = { ...this.QUESTIONS[question].answers };
    if (question in this.QUESTIONS) {
      const answers = Object.keys(this.QUESTIONS[question].answers);
      const shuffledQuestionsArr = this.randomizeArray(answers);
      const shuffledQuestions = {};
      shuffledQuestionsArr.forEach(key => {
        shuffledQuestions[key] = questionObject[key];
      });
      this.QUESTIONS[question].answers = shuffledQuestions;
    }
  };

  randomizeQuestions() {
    this.randomizeHelper(this.HELPFUL_INFO_QUESTION);
    this.randomizeHelper(this.PARTICULAR_FEATURES);
  }

  render() {
    const { withRandomizedQuestion } = this.props;
    const {
      currentQuestion,
      helpfulInfoAnswers,
      showProcessingScreen,
      particularFeaturesAnswers,
    } = this.state;

    const onAnswerSelected = () => {
      storeAnswerToStorage('hasInteractedWithSurvey', true);
      this.updateQuestionState(true);
    };

    const onBackClick = () => {
      const prevQuestionKey = this.QUESTIONS[this.state.currentQuestion].prev;
      trackBackButtonClicked(currentQuestion);

      if (prevQuestionKey) {
        this.setState({ currentQuestion: prevQuestionKey });
        storeAnswerToStorage(prevQuestionKey, null);
      }
    };

    const onOtherAnswerSelected = () => {
      let answerValue = this.QUESTIONS[currentQuestion].other_answer.key;
      if (currentQuestion === this.HELPFUL_INFO_QUESTION) {
        answerValue = helpfulInfoAnswers.sort();
      }
      if (currentQuestion === this.PARTICULAR_FEATURES) {
        answerValue = particularFeaturesAnswers.sort();
      }
      storeAnswerToStorage('hasInteractedWithSurvey', true);
      storeAnswerToStorage(currentQuestion, answerValue);
      trackQuestionAnswered(currentQuestion, answerValue);
      onAnswerSelected();
    };

    const changeOtherAnswerLabel = (name, nextFieldValue) => {
      if (name === this.HELPFUL_INFO_QUESTION) {
        this.setState({
          helpfulInfoAnswers: nextFieldValue,
        });
      }
      if (name === this.PARTICULAR_FEATURES) {
        this.setState({
          particularFeaturesAnswers: nextFieldValue,
        });
      }
    };

    const getQuestionCount = () => {
      return Object.keys(this.QUESTIONS).filter(question => !this.QUESTIONS[question].do_not_count)
        .length;
    };

    return (
      <Formik>
        <div className="survey-manager">
          {showProcessingScreen && <ProcessingScreen />}
          {currentQuestion && (
            <div className="text-left">
              <div className="lp-survey-step">
                <LpHeader
                  order={this.QUESTIONS[currentQuestion].order}
                  questionsCount={getQuestionCount()}
                />
                {withRandomizedQuestion && this.QUESTIONS[currentQuestion].multi_select_answers ? (
                  <MultipleSelectSurveyQuestion
                    question={this.QUESTIONS[currentQuestion]}
                    questionKey={currentQuestion}
                    onSelect={changeOtherAnswerLabel}
                  />
                ) : (
                  <LPSurveyQuestion
                    questionKey={currentQuestion}
                    question={this.QUESTIONS[currentQuestion]}
                    onAnswerSelected={onAnswerSelected}
                    withRandomizedQuestion={withRandomizedQuestion}
                  />
                )}
              </div>

              <div>
                {this.QUESTIONS[currentQuestion].prev && (
                  <Button
                    className="btn-link c-kale-500 width-100 col-5 text-left lp-survey-back-link"
                    label="Back"
                    onClick={onBackClick}
                  />
                )}
                {this.QUESTIONS[currentQuestion].other_answer && (
                  <Button
                    className="btn-link c-kale-500 width-100 col-7 text-right"
                    label={
                      (currentQuestion === this.HELPFUL_INFO_QUESTION &&
                        helpfulInfoAnswers.length > 0) ||
                      (currentQuestion === this.PARTICULAR_FEATURES &&
                        particularFeaturesAnswers.length > 0)
                        ? 'Next'
                        : this.QUESTIONS[currentQuestion].other_answer.text
                    }
                    onClick={onOtherAnswerSelected}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </Formik>
    );
  }
}

LpSurveyManager.propTypes = {
  onSurveyFinish: PropTypes.func.isRequired,
  withRandomizedQuestion: PropTypes.bool,
};

LpSurveyManager.defaultProps = {
  withRandomizedQuestion: false,
};

export default hot(module)(LpSurveyManager);
